import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import { IBV } from 'gatsby-plugin-bazaarvoice/types';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React, { useEffect, useState } from 'react';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FeedingCalculator from '../../../components/FeedingCalculator';
import BazaarvoiceWidget from '../../../components/bazaarvoice/BazaarvoiceWidget';
import IconList from '../../../components/common/IconList';
import ProductIngredients from '../../../components/product/ProductIngredients';
import { NodeProductProps } from '../types';

const TAB_FRAGMENT_MAP = {
  '#overview': 'overview',
  '#product': 'product',
  '#ingredients': 'ingredients',
  '#reviews': 'reviews',
};

interface Props {
  location: any;
  node: NodeProductProps;
  availableSizes: {
    text: string;
    icon: {
      url: string;
      alt: string;
    };
  }[];
  labelDeck?: string | undefined;
  showFeedingTab?:
    | IStorageFeedingCalculator
    | {
        processed: string;
      }
    | undefined;
  feedingCalculator?: IStorageFeedingCalculator;
  bvID: string;
}

const reviewsStop = css`
  scroll-margin-top: 95px;
  height: 1px;

  @media (min-width: 992px) {
    scroll-margin-top: 143px;
  }
`;

const StyledBorderedTableWrapper = styled(Row)`
  table {
    margin-bottom: 20px;
    table-layout: fixed;
    width: 100% !important;
    border-collapse: collapse;
    border-spacing: 0;

    tr {
      background-color: #f5f5f5;

      th {
        background-color: transparent;
        font-weight: 700;
        padding: 10px;
        text-align: left;
        vertical-align: inherit;

        p {
          margin-bottom: 0;
        }
      }
    }

    tbody {
      tr {
        td {
          padding: 10px;
          text-align: inherit;
          vertical-align: inherit;

          p {
            margin-bottom: 0;
          }
        }
        &:nth-child(odd) {
          background-color: #d9d9d9;
        }
      }
    }
  }
`;

const DEFAULT_TAB = 'overview';
// eslint-disable-next-line
const ProductTabs: React.FC<Props> = ({
  location,
  node,
  availableSizes,
  labelDeck,
  showFeedingTab,
  feedingCalculator,
  bvID,
}) => {
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const { t } = useTranslation();

  useEffect(() => {
    if (location.hash && location.hash in TAB_FRAGMENT_MAP) {
      setActiveTab(TAB_FRAGMENT_MAP[location.hash as keyof typeof TAB_FRAGMENT_MAP]);
    }
    // eslint-disable-next-line
  }, [location.hash]);

  const onTabSelect = (key: string | null) => {
    setActiveTab(key || DEFAULT_TAB);
    if (key) {
      sendIt({
        event: 'select_content',
        eventCategory: 'product details',
        eventAction: 'tabbed navigation',
        eventLabel: key,
        eventParams: {
          cta_location: 'product details',
          content_type: 'tabbed navigation',
          item_id: key,
          link_url: '(not set)',
        },
      });
    }
  };

  useEffect(() => {
    window.bvCallback = (bv: IBV) => {
      bv.swat_reviews.on('show', () => {
        setActiveTab(() => 'reviews');
        window.location.href = '#reviews';
      });
    };
  }, [location.hash]);

  return (
    <>
      <div id="reviews" className={reviewsStop} />
      <Tabs
        // defaultActiveKey={
        //   (TAB_FRAGMENT_MAP[location?.hash] as any) ? TAB_FRAGMENT_MAP[location?.hash] : 'overview'
        // }
        className="my-5 flex-nowrap overflow-auto"
        id="product-tabs"
        activeKey={activeTab}
        onSelect={key => onTabSelect(key)}
      >
        <Tab eventKey="overview" title={t('Overview')}>
          <Row>
            {node?.highlights.length > 0 && (
              <Col>
                <h2 className="h4">{t('Highlights')}</h2>
                <ul>
                  {node.highlights.map((highlight, index) => {
                    return <li key={index}>{highlight}</li>;
                  })}
                </ul>
              </Col>
            )}
            {availableSizes.length >= 1 && (
              <Col>
                <h2 className="h4">{t('Available Sizes')}</h2>
                <IconList items={availableSizes} size="small" />
              </Col>
            )}
            {node.body?.processed && (
              <Col sm={12}>
                <h2 className="h4">{t('Product Description')}</h2>
                {/* eslint-disable-next-line react/no-danger */}
                <div dangerouslySetInnerHTML={{ __html: node.body.processed }} />
              </Col>
            )}
          </Row>
        </Tab>
        {node?.relationships?.ingredients && node.relationships.ingredients.length > 1 && (
          <Tab eventKey="nutrition" title={t('Nutrition')}>
            <ProductIngredients
              title={t('Ingredients')}
              ingredients={node.relationships.ingredients}
              ingredientCode={node?.ingredientCode}
            />
            {labelDeck && (
              <div className="mt-4">
                <a
                  href={labelDeck}
                  target="_blank"
                  className="text-decoration-none"
                  rel="noreferrer"
                >
                  {t('Download the full ingredient list (PDF)')}
                </a>
              </div>
            )}
            {node?.guaranteedAnalysis && (
              <StyledBorderedTableWrapper className="guaranteed-analysis mt-4" md={12}>
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: node.guaranteedAnalysis.processed,
                  }}
                />
              </StyledBorderedTableWrapper>
            )}
          </Tab>
        )}
        {showFeedingTab && (
          <Tab eventKey="feeding" title={t('Feeding')}>
            {node.feeding_instructions && (
              <StyledBorderedTableWrapper>
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: node.feeding_instructions.processed,
                  }}
                />
              </StyledBorderedTableWrapper>
            )}
            {labelDeck && (
              <p>
                {t('For a list of all feeding recommendations')},{' '}
                <a href={labelDeck} target="_blank" rel="noreferrer">
                  {t('Download the full recommended feeding table')}
                </a>
                .
              </p>
            )}
            {feedingCalculator && node.relationships?.servingSize && (
              <FeedingCalculator
                storage={feedingCalculator}
                servingSize={node.relationships.servingSize}
              />
            )}
          </Tab>
        )}
        <Tab eventKey="reviews" title={t('Reviews')}>
          <BazaarvoiceWidget
            attributes={{
              productID: bvID,
              widgetID: 'reviews',
              seo: true,
            }}
          />
        </Tab>
      </Tabs>
    </>
  );
};

export default ProductTabs;
